import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, Building2, Store, X, Sparkles, Save } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

/**
 * iter70 · Super-Admin Plan Management.
 *
 * Single unified page that lets the platform operator change the plan
 * (Founding / Essentials / Professional / Signature — or the vendor
 * equivalents) for every tenant, and pin per-tenant JSON capability
 * overrides on top of that plan.
 *
 * Talks to the endpoints already shipped in iter70:
 *   GET   /super-admin/plans
 *   GET   /super-admin/clubs
 *   GET   /super-admin/vendors
 *   PATCH /super-admin/clubs/{id}/plan
 *   PATCH /super-admin/clubs/{id}/capability-overrides
 *   PATCH /super-admin/vendors/{id}/plan
 *   PATCH /super-admin/vendors/{id}/capability-overrides
 */
export default function SuperAdminPlansPage() {
  const [tab, setTab] = useState("clubs");
  const [catalog, setCatalog] = useState(null);
  const [clubs, setClubs] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [flash, setFlash] = useState(null);
  const [editing, setEditing] = useState(null); // { kind, id, name, overrides }
  const [filter, setFilter] = useState("");

  const reload = () => {
    setLoading(true);
    Promise.all([
      api.get("/super-admin/plans"),
      api.get("/super-admin/clubs"),
      api.get("/super-admin/vendors"),
    ])
      .then(([p, c, v]) => {
        setCatalog(p.data);
        setClubs(c.data || []);
        setVendors(v.data?.vendors || []);
      })
      .catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail) || err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { reload(); }, []);

  const flashOk = (msg) => {
    setFlash(msg);
    setTimeout(() => setFlash(null), 2500);
  };

  const changePlan = async (kind, id, plan) => {
    const path = kind === "club"
      ? `/super-admin/clubs/${id}/plan`
      : `/super-admin/vendors/${id}/plan`;
    try {
      await api.patch(path, { plan });
      if (kind === "club") {
        setClubs((cs) => cs.map((c) => c.club_id === id ? { ...c, plan } : c));
      } else {
        setVendors((vs) => vs.map((v) => v.vendor_id === id ? { ...v, plan } : v));
      }
      flashOk(`Plan updated → ${plan}`);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const saveOverrides = async (kind, id, overrides) => {
    const path = kind === "club"
      ? `/super-admin/clubs/${id}/capability-overrides`
      : `/super-admin/vendors/${id}/capability-overrides`;
    const { data } = await api.patch(path, overrides);
    if (kind === "club") {
      setClubs((cs) => cs.map((c) => c.club_id === id
        ? { ...c, capability_overrides: data.capability_overrides }
        : c));
    } else {
      setVendors((vs) => vs.map((v) => v.vendor_id === id
        ? { ...v, capability_overrides: data.capability_overrides }
        : v));
    }
    flashOk("Overrides saved");
  };

  const q = filter.trim().toLowerCase();
  const clubRows = useMemo(() => (
    q ? clubs.filter((c) => (c.name || "").toLowerCase().includes(q)
                          || (c.slug || "").toLowerCase().includes(q)) : clubs
  ), [clubs, q]);
  const vendorRows = useMemo(() => (
    q ? vendors.filter((v) => (v.company_name || v.name || "").toLowerCase().includes(q)
                            || (v.slug || "").toLowerCase().includes(q)) : vendors
  ), [vendors, q]);

  return (
    <div className="bg-cream min-h-screen" data-testid="sa-plans-page">
      <header className="bg-[#0f1a14] text-[#F9F8F4] border-b border-[#1A422D]">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-6 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <Sparkles size={22} className="text-[#D9C68A]" />
            <div>
              <div className="overline" style={{ color: "#D9C68A" }}>Platform · Capability Engine</div>
              <div className="font-serif text-2xl mt-0.5">Plans &amp; Overrides</div>
            </div>
          </div>
          <Link
            to="/super-admin"
            data-testid="sa-plans-back"
            className="inline-flex items-center gap-1.5 border border-[#D9C68A]/40 text-[#D9C68A] px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] hover:bg-[#1A422D] transition-colors"
          >
            <ChevronLeft size={14} /> Back
          </Link>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-10 space-y-6">
        {loading && <div className="overline text-[#7A7A7A]">Loading…</div>}
        {error && (
          <div className="px-4 py-3 text-sm border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a]" data-testid="sa-plans-error">
            {error}
          </div>
        )}
        {flash && (
          <div className="px-4 py-3 text-sm border border-[#1A422D] bg-[#EFF3EC] text-[#1A422D]" data-testid="sa-plans-flash">
            {flash}
          </div>
        )}

        <div className="flex items-center gap-2 flex-wrap">
          <TabButton active={tab === "clubs"} onClick={() => setTab("clubs")}
                     testId="sa-plans-tab-clubs" icon={<Building2 size={14} />}
                     count={clubs.length}>Clubs</TabButton>
          <TabButton active={tab === "vendors"} onClick={() => setTab("vendors")}
                     testId="sa-plans-tab-vendors" icon={<Store size={14} />}
                     count={vendors.length}>Vendors</TabButton>
          <div className="ml-auto">
            <input
              type="search"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter by name or slug…"
              data-testid="sa-plans-filter"
              className="input-field w-72"
            />
          </div>
        </div>

        {catalog && tab === "clubs" && (
          <TenantTable
            kind="club"
            rows={clubRows}
            plans={catalog.club_plans}
            allCapabilities={catalog.all_capabilities}
            planCapabilities={catalog.plan_capabilities}
            onChangePlan={changePlan}
            onEditOverrides={(row) => setEditing({
              kind: "club",
              id: row.club_id,
              name: row.name,
              plan: row.plan,
              overrides: row.capability_overrides || {},
            })}
          />
        )}

        {catalog && tab === "vendors" && (
          <TenantTable
            kind="vendor"
            rows={vendorRows}
            plans={catalog.vendor_plans}
            allCapabilities={catalog.all_capabilities}
            planCapabilities={catalog.plan_capabilities}
            onChangePlan={changePlan}
            onEditOverrides={(row) => setEditing({
              kind: "vendor",
              id: row.vendor_id,
              name: row.company_name || row.name,
              plan: row.plan,
              overrides: row.capability_overrides || {},
            })}
          />
        )}
      </div>

      {editing && catalog && (
        <OverridesDrawer
          entry={editing}
          allCapabilities={catalog.all_capabilities}
          planCapabilities={catalog.plan_capabilities}
          onClose={() => setEditing(null)}
          onSave={async (payload) => {
            try {
              await saveOverrides(editing.kind, editing.id, payload);
              setEditing(null);
            } catch (err) {
              throw new Error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
            }
          }}
        />
      )}
    </div>
  );
}

function TabButton({ active, onClick, children, icon, count, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-[0.22em] border transition-colors ${
        active
          ? "bg-[#1A422D] text-[#F9F8F4] border-[#1A422D]"
          : "bg-transparent text-[#1A1A1A] border-[#1A422D]/40 hover:bg-[#EFF3EC]"
      }`}
    >
      {icon}
      {children}
      <span className={`ml-1 text-[10px] px-1.5 py-0.5 rounded-full ${
        active ? "bg-[#0f1a14] text-[#D9C68A]" : "bg-[#E5E3D8] text-[#4A4A4A]"
      }`}>{count}</span>
    </button>
  );
}

function TenantTable({ kind, rows, plans, planCapabilities, onChangePlan, onEditOverrides }) {
  if (!rows.length) {
    return (
      <div className="surface-card p-10 text-center text-sm text-[#7A7A7A]" data-testid={`sa-plans-${kind}s-empty`}>
        No {kind}s to show.
      </div>
    );
  }
  return (
    <div className="surface-card overflow-x-auto" data-testid={`sa-plans-${kind}s-table`}>
      <table className="w-full text-left">
        <thead className="bg-[#F3F1E9]">
          <tr className="text-xs uppercase tracking-wider text-[#4A4A4A]">
            <th className="px-4 py-3">{kind === "club" ? "Club" : "Vendor"}</th>
            <th className="px-4 py-3">Plan</th>
            <th className="px-4 py-3">Overrides</th>
            <th className="px-4 py-3 w-40"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const id = kind === "club" ? row.club_id : row.vendor_id;
            const label = kind === "club"
              ? row.name
              : (row.company_name || row.name || "—");
            const sub = kind === "club" ? row.slug : row.contact_email;
            const currentPlan = row.plan || (kind === "club" ? "Club Founding" : "Vendor Founding");
            const overrides = row.capability_overrides || {};
            const overrideCount = Object.keys(overrides).length;
            return (
              <tr key={id} className="border-t border-[#E5E3D8] align-top" data-testid={`sa-plans-row-${id}`}>
                <td className="px-4 py-3">
                  <div className="text-sm text-[#1A1A1A]">{label}</div>
                  {sub && <div className="text-[11px] uppercase tracking-wider text-[#7A7A7A] mt-0.5">{sub}</div>}
                </td>
                <td className="px-4 py-3">
                  <select
                    value={currentPlan}
                    data-testid={`sa-plans-plan-select-${id}`}
                    onChange={(e) => onChangePlan(kind, id, e.target.value)}
                    className="input-field text-sm py-1.5"
                  >
                    {plans.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                  <div className="text-[10px] uppercase tracking-wider text-[#7A7A7A] mt-1">
                    {(planCapabilities[currentPlan] || []).length} capabilities
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="text-sm text-[#1A1A1A]" data-testid={`sa-plans-override-count-${id}`}>
                    {overrideCount === 0 ? "None" : `${overrideCount} custom`}
                  </div>
                  {overrideCount > 0 && (
                    <div className="text-[11px] text-[#4A4A4A] mt-1 font-mono break-all">
                      {Object.entries(overrides).slice(0, 3).map(([k, v]) => (
                        <span key={k} className="mr-2">{v ? "+" : "−"}{k}</span>
                      ))}
                      {overrideCount > 3 && <span className="text-[#7A7A7A]">…</span>}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    type="button"
                    onClick={() => onEditOverrides(row)}
                    data-testid={`sa-plans-edit-overrides-${id}`}
                    className="inline-flex items-center gap-1.5 border border-[#1A422D] text-[#1A422D] px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] hover:bg-[#1A422D] hover:text-[#F9F8F4] transition-colors"
                  >
                    Edit Overrides
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function OverridesDrawer({ entry, allCapabilities, planCapabilities, onClose, onSave }) {
  const [text, setText] = useState(() => JSON.stringify(entry.overrides || {}, null, 2));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const planCaps = new Set(planCapabilities[entry.plan] || []);

  const submit = async () => {
    setBusy(true);
    setErr(null);
    let parsed;
    try {
      parsed = text.trim() === "" ? {} : JSON.parse(text);
      if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
        throw new Error("Must be a JSON object like { \"canExportReports\": true }");
      }
    } catch (e) {
      setErr(`Invalid JSON: ${e.message}`);
      setBusy(false);
      return;
    }
    try {
      await onSave(parsed);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex justify-end"
      onClick={onClose}
      data-testid="sa-plans-drawer"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl bg-cream h-full overflow-y-auto shadow-2xl"
      >
        <div className="p-6 border-b border-[#E5E3D8] flex items-start justify-between gap-4">
          <div>
            <div className="overline text-[#7A7A7A]">Capability Overrides</div>
            <div className="font-serif text-2xl mt-1 text-[#1A1A1A]">{entry.name}</div>
            <div className="text-[11px] uppercase tracking-wider text-[#7A7A7A] mt-1">
              Plan: {entry.plan || "—"} · {planCaps.size} default capabilities
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="sa-plans-drawer-close"
            className="text-[#4A4A4A] hover:text-[#1A1A1A]"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div>
            <label className="overline text-[#4A4A4A]">JSON overrides</label>
            <p className="text-xs text-[#4A4A4A] mt-1 leading-relaxed">
              Object of <code>{"{ capability: true | false }"}</code>. <code>true</code> grants the capability
              on top of the plan; <code>false</code> revokes a capability the plan would otherwise grant.
              Unknown keys are dropped by the server.
            </p>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={14}
              data-testid="sa-plans-drawer-json"
              className="input-field font-mono text-xs mt-2 w-full"
              spellCheck={false}
            />
            {err && (
              <div className="mt-2 px-3 py-2 text-xs border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a]" data-testid="sa-plans-drawer-error">
                {err}
              </div>
            )}
          </div>

          <details className="text-xs text-[#4A4A4A]">
            <summary className="cursor-pointer overline text-[#4A4A4A]">
              Available capabilities ({allCapabilities.length})
            </summary>
            <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 font-mono text-[11px] max-h-64 overflow-y-auto border border-[#E5E3D8] p-3 bg-[#F3F1E9]">
              {allCapabilities.map((c) => (
                <div key={c} className={planCaps.has(c) ? "text-[#1A422D]" : "text-[#7A7A7A]"}>
                  {planCaps.has(c) ? "●" : "○"} {c}
                </div>
              ))}
            </div>
            <div className="mt-1 text-[10px] text-[#7A7A7A]">● = granted by current plan · ○ = not in current plan</div>
          </details>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={submit}
              disabled={busy}
              data-testid="sa-plans-drawer-save"
              className="inline-flex items-center gap-2 bg-[#1A422D] text-[#F9F8F4] px-5 py-2.5 text-xs uppercase tracking-[0.22em] hover:bg-[#0f1a14] disabled:opacity-50"
            >
              <Save size={14} /> {busy ? "Saving…" : "Save Overrides"}
            </button>
            <button
              type="button"
              onClick={onClose}
              data-testid="sa-plans-drawer-cancel"
              className="text-xs uppercase tracking-[0.22em] text-[#4A4A4A] hover:text-[#1A1A1A]"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
